import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PermissionSetList from '../components/lists/PermissionSetList';
import PermissionSetForm from '../components/forms/PermissionSetForm';
import PermissionSetDetails from '../components/details/PermissionSetDetails';
import Modal from '../components/common/Modal';
import apiService from '../services/api';
import { pageVariants, sectionVariants } from '../animations/variants';
import { useReadOnlyMode } from '../hooks/useReadOnlyMode';
import { ReadOnlyButton } from '../components/common/ReadOnlyButton';

// Both permission-set modals embed the inline-policy editor, which shows
// indented IAM JSON. At Modal's 600px default those lines wrap mid-token and
// the policy is unreadable. 900px matches the maxWidth="md" the app's other
// policy editors already use (e.g. CustomerManagedPolicies). The create and
// edit modals share one value so they cannot drift apart visually.
const PERMISSION_SET_MODAL_WIDTH = '900px';

// Policy attachments — both Prism-managed customer policies and customer-owned
// policy references — are attached in ONE place: the picker inside
// PermissionSetForm (create) and PermissionSetDetails' edit form. This page
// deliberately owns no second attach surface; a page-level dialog and a form
// accordion editing the same thing is how an admin ends up unable to tell what
// is actually attached.

const PermissionSets = ({
  awsManagedPolicies,
  getPermissionSet,
  createPermissionSet,
  updatePermissionSet,
  deletePermissionSet,
  fetchAwsManagedPolicies,
}) => {
  const [selectedPermissionSet, setSelectedPermissionSet] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [permissionSets, setPermissionSets] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    total: 0,
    totalPages: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  const dataFetchedRef = useRef(false);
  const location = useLocation();
  const isReadOnly = useReadOnlyMode();

  // Fetch permission sets with pagination
  const fetchPermissionSets = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const response = await apiService.getAllPermissionSets({
        page: pagination.page + 1, // API uses 1-indexed pages
        pageSize: pagination.pageSize,
        search: searchTerm || undefined,
      });
      if (response.success && response.data) {
        setPermissionSets(response.data.permissionSets || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
        }));
      }
    } catch (error) {
      console.error('Error fetching permission sets:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm]);

  useEffect(() => {
    if (location.state) {
      if (location.state.showForm) {
        setShowForm(true);
      }
      // Handle navigation from permission set assignments page
      if (location.state.selectPermissionSet) {
        handlePermissionSetClick(location.state.selectPermissionSet);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    fetchPermissionSets();
  }, [fetchPermissionSets]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 400);
  };

  const handleRefresh = async () => {
    await fetchPermissionSets();
  };

  const handlePermissionSetClick = async (id) => {
    const permissionSetData = await getPermissionSet(id);
    if (permissionSetData) {
      setSelectedPermissionSet(permissionSetData);
    }
  };

  const handleCreatePermissionSet = async (data) => {
    const success = await createPermissionSet(data);
    if (success) {
      setShowForm(false);
      fetchPermissionSets(); // Refresh the list
    }
    return success;
  };

  // Passthrough for the details editor. It MUST forward the payload verbatim.
  //
  // The backend distinguishes an update request that OMITS `policy_reference_ids`
  // ("the caller did not mention references — leave the attachments alone") from
  // one that sends `[]` ("detach every reference"), and it does the same for
  // `customer_managed_policy_ids`. PermissionSetDetails builds its payload by
  // comparing the server's baseline with the admin's selection and only names a
  // key when that kind actually changed, so JSON.stringify drops the untouched
  // one, Go decodes nil, and an ordinary "rename the permission set" save cannot
  // detach anything. Defaulting a key here — even to `[]` — would turn every
  // such save into a silent mass detach.
  const handlePermissionSetUpdate = async (id, data) => {
    const success = await updatePermissionSet(id, data);
    if (success) {
      // Re-read the permission set so the open modal shows the attachments the
      // server actually recorded, and so the edit form's baseline is refreshed
      // before the next save.
      const refreshed = await getPermissionSet(id);
      if (refreshed) setSelectedPermissionSet(refreshed);
      fetchPermissionSets();
    }
    return success;
  };

  const handlePermissionSetDelete = async (id, name) => {
    await deletePermissionSet(id, name);
    setSelectedPermissionSet(null);
    fetchPermissionSets(); // Refresh the list
  };

  const fetchPoliciesIfNeeded = useCallback(async () => {
    try {
      const policies = await fetchAwsManagedPolicies();
      return policies;
    } catch (error) {
      console.error('Error fetching AWS policies:', error);
      return [];
    }
  }, [fetchAwsManagedPolicies]);

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      style={{
        minHeight: '100vh',
        paddingTop: 32,
        paddingBottom: 32,
        paddingLeft: 32,
        paddingRight: 32,
        backgroundColor: '#F9FBFD',
      }}
    >
      {/* Page Header */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          border: '1px solid #E5E5E5',
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              sx={{
                backgroundColor: '#27AE60',
                width: 48,
                height: 48,
              }}
            >
              <SecurityIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: 'Bitter, Georgia, serif',
                  fontWeight: 600,
                  fontSize: '1.75rem',
                  color: '#253E66',
                }}
              >
                Permission Sets
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  color: '#999999',
                  fontWeight: 500,
                }}
              >
                {pagination.total} permission set
                {pagination.total !== 1 ? 's' : ''} in the system
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search permission sets..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 250,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#F9FBFD',
                  borderRadius: '6px',
                  fontFamily: 'Metropolis, sans-serif',
                  fontSize: '0.875rem',
                  '& fieldset': {
                    borderColor: '#E5E5E5',
                  },
                  '&:hover fieldset': {
                    borderColor: '#27AE60',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#27AE60',
                  },
                },
              }}
            />
            <Tooltip title="Refresh Permission Sets">
              <IconButton
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  color: '#666666',
                  backgroundColor: '#F9FBFD',
                  border: '1px solid #E5E5E5',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#F0F4F8',
                    borderColor: '#27AE60',
                    color: '#27AE60',
                  },
                  '&:disabled': {
                    color: '#BDBDBD',
                    backgroundColor: '#F9FBFD',
                  },
                }}
              >
                {isRefreshing ? (
                  <CircularProgress size={20} sx={{ color: '#27AE60' }} />
                ) : (
                  <RefreshIcon />
                )}
              </IconButton>
            </Tooltip>

            <ReadOnlyButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowForm(true)}
              disabled={isReadOnly}
              sx={{
                backgroundColor: '#27AE60',
                borderRadius: '6px',
                fontFamily: 'Metropolis, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                '&:hover': {
                  backgroundColor: '#219A52',
                },
              }}
            >
              Create Permission Set
            </ReadOnlyButton>
          </Box>
        </Box>
      </Paper>
      </motion.div>

      {/* Permission Sets List */}
      <motion.div variants={sectionVariants}>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          overflow: 'hidden',
          minHeight: '400px',
        }}
      >
        <PermissionSetList
          permissionSets={permissionSets}
          onPermissionSetClick={handlePermissionSetClick}
          loading={isRefreshing}
        />

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={(e, newPage) => setPagination(prev => ({ ...prev, page: newPage }))}
          rowsPerPage={pagination.pageSize}
          onRowsPerPageChange={(e) =>
            setPagination(prev => ({ ...prev, pageSize: parseInt(e.target.value, 10), page: 0 }))
          }
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{
            borderTop: '1px solid #E5E5E5',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Metropolis, sans-serif',
            },
          }}
        />
      </Paper>
      </motion.div>

      {/* Modals */}
      <AnimatePresence>
        {showForm && (
          <Modal onClose={() => setShowForm(false)} key="ps-form-modal" maxWidth={PERMISSION_SET_MODAL_WIDTH}>
            <PermissionSetForm
              awsManagedPolicies={awsManagedPolicies}
              onSubmit={handleCreatePermissionSet}
              onCancel={() => setShowForm(false)}
              fetchAwsPolicies={fetchPoliciesIfNeeded}
            />
          </Modal>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedPermissionSet && (
          <Modal onClose={() => setSelectedPermissionSet(null)} key="ps-details-modal" maxWidth={PERMISSION_SET_MODAL_WIDTH}>
            <PermissionSetDetails
              permissionSet={selectedPermissionSet}
              awsManagedPolicies={awsManagedPolicies}
              onClose={() => setSelectedPermissionSet(null)}
              onUpdate={handlePermissionSetUpdate}
              onDelete={handlePermissionSetDelete}
              fetchAwsPolicies={fetchPoliciesIfNeeded}
            />
          </Modal>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default PermissionSets;
